import React, { useEffect, useRef, useState, createContext, useContext } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { slugify } from "./_shared";
import AdmissionInquiryModal from "./AdmissionInquiryModal";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import AboutMenu from "@/components/AboutMenu";
import {
  Phone, Mail, MapPin, ExternalLink, Menu, X,
  Facebook, Instagram, Linkedin, Twitter, Youtube, ArrowUpRight,
  ChevronDown, GraduationCap, Sparkles,
} from "lucide-react";

const SLUG = "cidco";

const Ctx = createContext(null);
export const useCidco = () => useContext(Ctx);

// Flat nav (mirrors /polyaurangabad and /foodtech)
const NAV = [
  { to: "", end: true, label: "Home" },
  { to: "about", label: "About" },
  { to: "departments", label: "Departments" },
  { to: "campus-engagements", label: "Campus Engagements" },
  { to: "committees", label: "Committees & Cells" },
  { to: "gallery", label: "Gallery" },
  { to: "facilities", label: "Facilities" },
  { to: "academics", label: "Academics" },
  { to: "alumni", label: "Alumni" },
  { to: "careers", label: "Careers" },
  { to: "fra-ffc", label: "FRA/FFC" },
  { to: "contact", label: "Contact" },
];

export default function CidcoLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/website/${SLUG}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Website not configured");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-[#0B2447] font-['Inter',sans-serif]">
        <Sparkles className="animate-pulse" size={40} strokeWidth={1.5} />
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4 font-['Inter',sans-serif]">
        <div className="text-center max-w-md">
          <Sparkles className="mx-auto text-[#0B2447]" size={36} strokeWidth={1.5} />
          <h1 className="mt-4 font-['Poppins',sans-serif] font-bold text-2xl text-[#0F172A]">Website unavailable</h1>
          <p className="mt-2 text-sm text-[#475569]">{err || "Please try again later."}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute || {};
  const logoSrc = inst.logo_url || "/assets/mitcsn-logo.png";

  return (
    <Ctx.Provider value={data}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@500;600;700;800&display=swap"
        />

        {/* Primary */}
        <title>Marathwada Institute of Technology, CIDCO | GS Mandal</title>
        <meta name="description" content="Marathwada Institute of Technology, CIDCO, Chhatrapati Sambhajinagar, by GS Mandal. Offering quality education with a Quest for Excellence." />
        <meta name="keywords" content="Marathwada Institute of Technology, MIT CIDCO, MIT Aurangabad, MIT Chhatrapati Sambhajinagar, GS Mandal, Engineering College Aurangabad, Engineering College Sambhajinagar, Best Engineering College in Aurangabad, Best Engineering College in Maharashtra, Engineering Admission Aurangabad, Computer Science Engineering, CSE Engineering, Computer Engineering, Information Technology, IT Engineering, Artificial Intelligence, Artificial Intelligence and Data Science, AI and DS, Data Science Engineering, Electronics Engineering, Electronics and Telecommunication Engineering, ENTC Engineering, Electrical Engineering, Mechanical Engineering, Civil Engineering, Automobile Engineering, Production Engineering, Chemical Engineering, Diploma Engineering, Polytechnic College, Polytechnic Aurangabad, Diploma in Computer Engineering, Diploma in Information Technology, Diploma in Mechanical Engineering, Diploma in Civil Engineering, Diploma in Electrical Engineering, Diploma in Electronics Engineering, Bachelor of Engineering, BE Degree, BTech Degree, Engineering Courses Maharashtra, Professional Courses, Technical Education, Engineering Programs, Undergraduate Engineering, Postgraduate Engineering, MBA, Master of Business Administration, Management Studies, Postgraduate Programs, Dr Babasaheb Ambedkar Marathwada University, DBATU, Engineering Education, Higher Education Maharashtra, Career Oriented Education, Industry Oriented Education, Placement Assistance, Campus Placement, Engineering Internship, Research and Innovation, Quest for Excellence, GS Mandal Educational Institutions, College in CIDCO, College in Aurangabad, College in Sambhajinagar, Engineering Institute Maharashtra, Technical Institute Maharashtra" />
        <meta name="author" content="GS Mandal" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://gsmandal.org/cidco" />

        {/* Open Graph / Facebook / WhatsApp / LinkedIn */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Marathwada Institute of Technology, CIDCO | GS Mandal" />
        <meta property="og:description" content="Quest for Excellence in Education at MIT CIDCO, Chhatrapati Sambhajinagar." />
        <meta property="og:url" content="https://gsmandal.org/cidco" />
        <meta property="og:site_name" content="GS Mandal" />
        <meta property="og:image" content="https://gsmandal.org/assets/og/mit-cidco-og.jpg" />
        <meta property="og:image:secure_url" content="https://gsmandal.org/assets/og/mit-cidco-og.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Marathwada Institute of Technology CIDCO campus by GS Mandal" />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Marathwada Institute of Technology, CIDCO | GS Mandal" />
        <meta name="twitter:description" content="Quest for Excellence in Education at MIT CIDCO, Chhatrapati Sambhajinagar." />
        <meta name="twitter:image" content="https://gsmandal.org/assets/og/mit-cidco-og.jpg" />
        <meta name="twitter:image:alt" content="Marathwada Institute of Technology CIDCO campus by GS Mandal" />

        {/* Mobile / Browser */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#0D2B52" />
        <meta name="format-detection" content="telephone=no" />

        {/* Favicon (scoped to /cidco only; restored to global on route change) */}
        <link rel="icon" href="/assets/cidco/favicon.ico" />
        <link rel="apple-touch-icon" href="/assets/cidco/apple-touch-icon.png" />

        {/* Structured Data */}
        <script type="application/ld+json">{`
{
  "@context": "https://schema.org",
  "@type": "CollegeOrUniversity",
  "name": "Marathwada Institute of Technology, CIDCO",
  "alternateName": "MIT CIDCO",
  "url": "https://gsmandal.org/cidco",
  "logo": "https://gsmandal.org/assets/logo.png",
  "image": "https://gsmandal.org/assets/og/mit-cidco-og.jpg",
  "description": "Marathwada Institute of Technology, CIDCO, Chhatrapati Sambhajinagar, by GS Mandal. Quest for Excellence in Education.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Chhatrapati Sambhajinagar",
    "addressRegion": "Maharashtra",
    "addressCountry": "IN"
  },
  "parentOrganization": {
    "@type": "Organization",
    "name": "GS Mandal",
    "url": "https://gsmandal.org"
  }
}
        `}</script>
      </Helmet>

      <div className="min-h-screen flex flex-col bg-white text-[#0F172A] font-['Inter',sans-serif]">
        {/* Utility top bar */}
        <div className="bg-[#06183A] text-white/85 text-[12px]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-5 flex-wrap">
              {inst.admission_phone && (
                <a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-[#06B6D4]" data-testid="cid-top-phone">
                  <Phone size={11} strokeWidth={1.8} className="text-[#06B6D4]" />{inst.admission_phone}
                </a>
              )}
              {inst.email && (
                <a href={`mailto:${inst.email}`} className="inline-flex items-center gap-1.5 hover:text-[#06B6D4]" data-testid="cid-top-email">
                  <Mail size={11} strokeWidth={1.8} className="text-[#06B6D4]" />{inst.email}
                </a>
              )}
              {inst.city && (
                <span className="hidden md:inline-flex items-center gap-1.5">
                  <MapPin size={11} strokeWidth={1.8} className="text-[#06B6D4]" />{inst.city}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <InstituteSwitcher currentSlug="cidco" testidPrefix="cid-top-switcher" />
              {inst.facebook_url && (<a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="cid-top-facebook" className="hover:text-[#06B6D4]"><Facebook size={12} strokeWidth={1.8} /></a>)}
              {inst.instagram_url && (<a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="cid-top-instagram" className="hover:text-[#06B6D4]"><Instagram size={12} strokeWidth={1.8} /></a>)}
              {inst.linkedin_url && (<a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" data-testid="cid-top-linkedin" className="hover:text-[#06B6D4]"><Linkedin size={12} strokeWidth={1.8} /></a>)}
              {inst.twitter_url && (<a href={inst.twitter_url} target="_blank" rel="noopener noreferrer" data-testid="cid-top-twitter" className="hover:text-[#06B6D4]"><Twitter size={12} strokeWidth={1.8} /></a>)}
              {inst.youtube_url && (<a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="cid-top-youtube" className="hover:text-[#06B6D4]"><Youtube size={12} strokeWidth={1.8} /></a>)}
              <a href="https://erp.mit.asia" target="_blank" rel="noopener noreferrer" data-testid="cid-top-erp"
                 className="inline-flex items-center gap-1 bg-[#FF6B1A] hover:bg-[#E55400] text-white px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold">
                Student ERP <ArrowUpRight size={10} strokeWidth={2.5} />
              </a>
              {(data?.naac_documents || []).length > 0 && (
                <NavLink to="naac" data-testid="cid-top-naac"
                  className="inline-flex items-center gap-1 bg-cyan-300 hover:bg-cyan-200 text-[#0B2447] px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ring-1 ring-cyan-400">
                  NAAC
                </NavLink>
              )}
            </div>
          </div>
        </div>

        {/* Header band */}
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E2E8F0]">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-1 md:grid-cols-[minmax(0,19fr)_minmax(0,63fr)_minmax(0,18fr)] items-center gap-4">
            <NavLink to="" end className="flex items-center justify-center md:justify-start" data-testid="cid-logo-link">
              <img src={logoSrc} alt={inst.name} className="h-[96px] md:h-[112px] w-auto max-w-full object-contain" />
            </NavLink>
            <div className="text-center leading-tight">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#06B6D4] font-semibold" data-testid="cid-header-trust">
                Gramaudyogik Shikshan Mandal&apos;s
              </div>
              <div className="font-['Poppins',sans-serif] font-bold text-xl md:text-3xl text-[#0B2447] uppercase mt-1" data-testid="cid-header-name">
                {inst.name}
              </div>
              {inst.city && (
                <div className="font-['Poppins',sans-serif] text-base md:text-lg text-[#1B3567] mt-1" data-testid="cid-header-city">
                  {inst.city}
                </div>
              )}
              {inst.tagline && (
                <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#FF6B1A] italic mt-2">{inst.tagline}</div>
              )}
            </div>
            <div className="hidden md:block text-right">
              {inst.code && (
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#475569]" data-testid="cid-header-code">
                  <span className="text-[#0B2447] font-semibold">Institute Code</span> · {inst.code}
                </div>
              )}
              <div className="text-xs text-[#475569] mt-0.5">
                AICTE Approved{inst.year_of_establishment ? ` · Estd. ${inst.year_of_establishment}` : ""}
              </div>
              {inst.mandatory_disclosure_url && (
                <a href={inst.mandatory_disclosure_url} target="_blank" rel="noopener noreferrer"
                   data-testid="cid-header-mandatory-disclosure"
                   className="mt-1 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#FF6B1A] hover:text-[#0B2447] underline underline-offset-2">
                  Mandatory Disclosure <ExternalLink size={10} strokeWidth={2} />
                </a>
              )}
            </div>
          </div>

          {/* Main nav — navy strip with mega-menu dropdowns */}
          <nav className="bg-[#0B2447] shadow-md" data-testid="cid-nav">
            <div className="max-w-7xl mx-auto px-4">
              <div className="hidden xl:flex items-center gap-1 py-2 flex-wrap">
                {NAV.map((n) =>
                  n.to === "departments" ? (
                    <DepartmentsMegaMenu key="departments" departments={data.departments || []} />
                  ) : n.to === "campus-engagements" ? (
                    <EngagementsDropdown key="campus-engagements" events={data.events || []} />
                  ) : n.to === "about" ? (
                    <AboutMenu
                      key="about"
                      linkClass={navLinkClass}
                      testidPrefix="cid-nav-about"
                      menuClassName="bg-white text-slate-800 ring-1 ring-slate-200"
                      itemHoverClassName="hover:bg-cyan-50 text-slate-800"
                    />
                  ) : (
                    <NavLink key={n.to} to={n.to} end={n.end} data-testid={`cid-nav-${n.to || "home"}`} className={navLinkClass}>
                      {n.label}
                    </NavLink>
                  )
                )}
              </div>
              <div className="xl:hidden flex items-center justify-between py-3">
                <span className="text-white font-medium text-sm">Menu</span>
                <button type="button" onClick={() => setMobileOpen((v) => !v)} className="p-2 text-white" aria-label="Toggle menu" data-testid="cid-nav-toggle">
                  {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
              </div>
              {mobileOpen && (
                <div className="xl:hidden grid grid-cols-2 gap-1 pb-3">
                  {NAV.map((n) =>
                    n.to === "departments" ? (
                      <React.Fragment key="departments">
                        <NavLink to="departments" onClick={() => setMobileOpen(false)} className={navLinkClass}>Departments</NavLink>
                        {(data.departments || []).length > 0 && (
                          <div className="col-span-2 -mt-1 mb-1 pl-4 grid grid-cols-1 gap-0.5">
                            {(data.departments || []).map((d) => (
                              <NavLink key={d.id} to={`departments/${slugify(d.name)}`} onClick={() => setMobileOpen(false)}
                                className="text-[12px] text-white/80 hover:text-[#67E8F9] py-1.5 border-l-2 border-white/15 pl-3">
                                {d.name}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    ) : n.to === "campus-engagements" ? (
                      <React.Fragment key="campus-engagements">
                        <NavLink to="campus-engagements" onClick={() => setMobileOpen(false)} className={navLinkClass}>Campus Engagements</NavLink>
                        {(() => {
                          const types = uniqueTypesWithCount(data.events || []);
                          return types.length > 0 ? (
                            <div className="col-span-2 -mt-1 mb-1 pl-4 grid grid-cols-1 gap-0.5">
                              {types.map((t) => (
                                <NavLink key={t.slug} to={`campus-engagements/${t.slug}`} onClick={() => setMobileOpen(false)}
                                  className="text-[12px] text-white/80 hover:text-[#67E8F9] py-1.5 border-l-2 border-white/15 pl-3">
                                  {t.label} ({t.count})
                                </NavLink>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </React.Fragment>
                    ) : n.to === "about" ? (
                      <AboutMenu
                        key="about"
                        linkClass={navLinkClass}
                        testidPrefix="cid-nav-about"
                        menuClassName="bg-white text-slate-800 ring-1 ring-slate-200"
                        itemHoverClassName="hover:bg-cyan-50 text-slate-800"
                      />
                    ) : (
                      <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)} className={navLinkClass}>
                        {n.label}
                      </NavLink>
                    )
                  )}
                  {(data?.naac_documents || []).length > 0 && (
                    <NavLink to="naac" onClick={() => setMobileOpen(false)} data-testid="cid-nav-naac-mobile"
                      className="col-span-2 inline-flex items-center justify-center gap-1.5 bg-cyan-300 hover:bg-cyan-200 text-slate-900 font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-sm shadow-sm ring-2 ring-cyan-300">
                      NAAC
                    </NavLink>
                  )}
                  <button type="button" onClick={() => { setMobileOpen(false); setInquiryOpen(true); }} data-testid="cid-nav-apply-mobile"
                    className="col-span-2 mt-2 inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#FF6B1A] to-[#E55400] text-white font-semibold text-xs uppercase tracking-wider px-3 py-2 rounded-lg shadow-md">
                    Apply Now
                  </button>
                </div>
              )}
            </div>
          </nav>

          {Array.isArray(inst.scrollers) && inst.scrollers.length > 0 && (
            <ScrollerBar items={inst.scrollers} />
          )}
        </header>

        <main className="flex-1">
          <Outlet />
        </main>

        {inst.whatsapp_phone && (
          <WhatsAppFab phone={inst.whatsapp_phone.replace(/[^0-9]/g, "")} message={`Hello ${inst.name || "team"}, I have an admission inquiry.`} />
        )}
        <AdmissionInquiryFab onClick={() => setInquiryOpen(true)} />
        <AdmissionInquiryModal
          open={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          slug={inst.website_slug}
          departments={data.departments || []}
        />

        {/* Footer */}
        <footer className="bg-[#06183A] text-white/80 mt-16">
          <div className="max-w-7xl mx-auto px-4 py-12 grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 text-[#06B6D4] font-semibold">
                <GraduationCap size={18} strokeWidth={2} />
                <span className="uppercase tracking-[0.2em] text-xs">{inst.name || "MIT CIDCO"}</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                A flagship engineering campus of Gramaudyogik Shikshan Mandal — building India&apos;s next generation of technologists.
              </p>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm font-['Poppins',sans-serif]">Explore</div>
              <ul className="space-y-2 text-sm">
                {[
                  { to: "about", l: "About" },
                  { to: "vision-mission", l: "Vision & Mission" },
                  { to: "leadership", l: "Leadership" },
                  { to: "departments", l: "Departments" },
                  { to: "admissions", l: "Admissions" },
                  { to: "contact", l: "Contact" },
                ].map((p) => (
                  <li key={p.to}><NavLink to={p.to} className="hover:text-[#06B6D4]">{p.l}</NavLink></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm font-['Poppins',sans-serif]">Engage</div>
              <ul className="space-y-2 text-sm">
                <li><NavLink to="campus-engagements" className="hover:text-[#06B6D4]">Campus Engagements</NavLink></li>
                <li><NavLink to="committees" className="hover:text-[#06B6D4]">Committees &amp; Cells</NavLink></li>
                <li><NavLink to="gallery" className="hover:text-[#06B6D4]">Gallery</NavLink></li>
                <li><NavLink to="notices" className="hover:text-[#06B6D4]">Notices</NavLink></li>
                <li><NavLink to="links" className="hover:text-[#06B6D4]">Important Links</NavLink></li>
                <li><NavLink to="facilities" className="hover:text-[#06B6D4]">Facilities</NavLink></li>
              </ul>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm font-['Poppins',sans-serif]">Reach Us</div>
              <ul className="space-y-2 text-sm">
                {(inst.address1 || inst.city) && (
                  <li className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 text-[#06B6D4] shrink-0" />
                    {[inst.address1, inst.address2, [inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(" "), inst.state, inst.country || "INDIA"].filter(Boolean).join(", ")}
                  </li>
                )}
                {(inst.contact_phone || inst.admission_phone) && (
                  <li className="flex items-start gap-2"><Phone size={13} className="mt-0.5 text-[#06B6D4]" />
                    {[inst.contact_phone, inst.admission_phone].filter(Boolean).join(" · ")}
                  </li>
                )}
                {inst.email && (
                  <li className="flex items-start gap-2"><Mail size={13} className="mt-0.5 text-[#06B6D4]" />
                    <a href={`mailto:${inst.email}`} className="hover:text-[#06B6D4]">{inst.email}</a>
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 py-4 text-center text-[11px] text-white/60">
            © {new Date().getFullYear()} {inst.name} · Gramaudyogik Shikshan Mandal. All rights reserved.
          </div>
        </footer>
      </div>
    </Ctx.Provider>
  );
}

const navLinkClass = ({ isActive }) =>
  `text-sm px-3 py-2 rounded-md transition-colors whitespace-nowrap font-medium ${
    isActive
      ? "bg-[#06B6D4] text-white shadow-sm"
      : "text-white/90 hover:bg-white/10 hover:text-white"
  }`;

function DepartmentsMegaMenu({ departments }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 150); };

  const sorted = [...departments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div ref={wrapRef} className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid="cid-nav-departments-wrap"
    >
      <NavLink to="departments" data-testid="cid-nav-departments"
        className={({ isActive }) => `${navLinkClass({ isActive })} inline-flex items-center gap-1`}
        onClick={() => setOpen(false)}>
        Departments
        <ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>
      {open && sorted.length > 0 && (
        <div className="absolute left-0 top-full pt-2 z-50"
             data-testid="cid-nav-departments-menu"
             onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <div className="bg-white rounded-xl shadow-2xl ring-1 ring-[#E2E8F0] p-5 w-[640px]">
            <div className="flex items-center justify-between mb-4 border-b border-[#E2E8F0] pb-3">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#06B6D4] font-semibold">Academic Programmes</div>
                <div className="font-['Poppins',sans-serif] font-bold text-lg text-[#0B2447]">Explore Departments</div>
              </div>
              <NavLink to="departments" onClick={() => setOpen(false)}
                className="text-[11px] uppercase tracking-wider font-semibold text-[#FF6B1A] hover:text-[#0B2447] inline-flex items-center gap-1">
                View All <ArrowUpRight size={12} />
              </NavLink>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {sorted.map((d) => (
                <NavLink key={d.id} to={`departments/${slugify(d.name)}`}
                  data-testid={`cid-nav-dept-${d.id}`}
                  onClick={() => setOpen(false)}
                  className="group flex items-start gap-3 p-3 rounded-lg hover:bg-[#F4F6FA] transition-colors">
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-[#0B2447] to-[#1B3567] text-[#06B6D4] flex items-center justify-center group-hover:from-[#06B6D4] group-hover:to-[#0891B2] group-hover:text-white transition-colors">
                    <GraduationCap size={16} strokeWidth={1.5} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-[#0B2447] group-hover:text-[#FF6B1A] transition-colors">{d.name}</span>
                    {d.code && <span className="block text-[10px] text-[#475569] font-mono uppercase tracking-wider mt-0.5">{d.code}</span>}
                  </span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function uniqueTypesWithCount(events) {
  const map = new Map();
  for (const e of events) {
    const label = (e.event_type || "").trim();
    if (!label) continue;
    const slug = slugify(label);
    if (map.has(slug)) map.set(slug, { ...map.get(slug), count: map.get(slug).count + 1 });
    else map.set(slug, { label, slug, count: 1 });
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function EngagementsDropdown({ events }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 150); };

  const types = uniqueTypesWithCount(events);

  return (
    <div ref={wrapRef} className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid="cid-nav-engagements-wrap">
      <NavLink to="campus-engagements" data-testid="cid-nav-campus-engagements"
        className={({ isActive }) => `${navLinkClass({ isActive })} inline-flex items-center gap-1`}
        onClick={() => setOpen(false)}>
        Campus Engagements
        {types.length > 0 && (<ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />)}
      </NavLink>
      {open && types.length > 0 && (
        <div className="absolute left-0 top-full pt-2 min-w-[260px] z-50"
             data-testid="cid-nav-engagements-menu"
             onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <div className="bg-white rounded-xl shadow-2xl ring-1 ring-[#E2E8F0] py-2">
            <NavLink to="campus-engagements" data-testid="cid-nav-engagements-all"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-[#0B2447] hover:bg-[#F4F6FA] border-b border-[#E2E8F0]">
              <div className="font-semibold">All Stories</div>
              <div className="text-[10px] uppercase tracking-wider text-[#475569] mt-0.5">Show every type</div>
            </NavLink>
            {types.map((t) => (
              <NavLink key={t.slug} to={`campus-engagements/${t.slug}`}
                data-testid={`cid-nav-engagement-type-${t.slug}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-[#0B2447] hover:bg-[#F4F6FA]">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{t.label}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#06B6D4] font-mono font-semibold">{t.count}</span>
                </div>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppFab({ phone, message }) {
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      aria-label="Chat on WhatsApp for admission inquiry"
      data-testid="cid-whatsapp-fab"
      className="fixed z-40 bottom-24 right-5 sm:bottom-28 sm:right-7 flex items-center gap-2 group">
      <span className="absolute inline-flex h-14 w-14 rounded-full bg-green-500 opacity-40 animate-ping pointer-events-none right-0 bottom-0" />
      <span className="hidden sm:inline-flex bg-white text-[#0B2447] text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-[#E2E8F0] opacity-0 group-hover:opacity-100 transition-opacity">
        Admission inquiry
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
          <path d="M20.52 3.48A11.78 11.78 0 0 0 12.06 0C5.5 0 .15 5.34.15 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.91-5.34 11.91-11.9 0-3.18-1.24-6.17-3.45-8.42ZM12.06 21.8h-.01a9.85 9.85 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.65-.23-.37a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.13 1.03 7 2.9a9.87 9.87 0 0 1 2.9 7c0 5.46-4.44 9.89-9.9 9.89Zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5l-.56-.01c-.2 0-.5.07-.76.37-.27.3-1.02.99-1.02 2.42 0 1.43 1.04 2.81 1.18 3 .15.2 2.06 3.15 5 4.42.7.3 1.24.48 1.66.62.7.22 1.34.19 1.84.12.56-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
        </svg>
      </span>
    </a>
  );
}

function AdmissionInquiryFab({ onClick }) {
  return (
    <button type="button" onClick={onClick}
      aria-label="Open admission inquiry form"
      data-testid="cid-admission-inquiry-fab"
      className="fixed z-40 bottom-5 right-5 sm:bottom-7 sm:right-7 inline-flex items-center gap-2 group">
      <span className="hidden sm:inline-flex bg-white text-[#0B2447] text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-[#E2E8F0] opacity-0 group-hover:opacity-100 transition-opacity">
        Contact Admissions
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-[#FF6B1A] to-[#E55400] hover:from-[#E55400] hover:to-[#C24A00] text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
          <path d="M22 10v6" />
          <path d="M2 10l10-5 10 5-10 5z" />
          <path d="M6 12v5c3 3 9 3 12 0v-5" />
        </svg>
      </span>
    </button>
  );
}

function ScrollerBar({ items }) {
  const list = items || [];
  if (list.length === 0) return null;
  const renderSegment = (s, i) => {
    const inner = (
      <span className="inline-block whitespace-nowrap px-3">
        <span className="opacity-80 mr-2 align-middle">▸</span>
        {s.text}
      </span>
    );
    if (s.url) {
      return (
        <a key={`${s.id}-${i}`} href={s.url} target="_blank" rel="noopener noreferrer"
          className="hover:underline focus:underline outline-none"
          data-testid={`cid-scroller-link-${s.id}`}>
          {inner}
        </a>
      );
    }
    return <span key={`${s.id}-${i}`} data-testid={`cid-scroller-text-${s.id}`}>{inner}</span>;
  };
  const duration = Math.max(20, Math.min(60, list.length * 12));
  return (
    <div className="bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white border-y border-[#06B6D4]/40 overflow-hidden" data-testid="cid-scroller-bar">
      <style>{`
        @keyframes cid-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        [data-testid="cid-scroller-bar"]:hover [data-testid="cid-scroller-track"] {
          animation-play-state: paused !important;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-3 py-1.5 flex items-center gap-3">
        <span className="hidden sm:inline-flex text-[10px] tracking-[0.25em] uppercase font-bold bg-white/20 px-2 py-0.5 rounded shrink-0">
          Announcement
        </span>
        <div className="flex-1 overflow-hidden">
          <span data-testid="cid-scroller-track"
            className="inline-block whitespace-nowrap text-sm font-medium"
            style={{ animation: `cid-marquee ${duration}s linear infinite` }}>
            {list.map(renderSegment)}
            {list.map((s, i) => renderSegment(s, `copy-${i}`))}
          </span>
        </div>
      </div>
    </div>
  );
}
